import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { PullRequestDetails, TestExecutionDocument } from '@finvari/deliver';
import { TestLaneDef, LaneStatus } from '../../../workflow/workflowConfig';
import { laneRequired } from '../../../workflow/prWorkflow';
import { laneStatusGlyph, laneStatusLabel } from './statusIcons';
import { useDeploymentStatus } from '../../../contexts/DeploymentStatusContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { useTestSuites } from '../../../utils/testUtils';
import { subscribeToTestExecutionsForPR, startTestExecution, waiveTestRerun, verifyTestExecution } from '../../../firebase';
import { formatDateTime } from '../../../utils/deploymentUtils';
import UserProfileDisplay from '../../../components/UserProfileDisplay';
import TestSubjectSelectionDialog from '../../environments/testExecutions/TestSubjectSelectionDialog';
import { usePatternAcks } from '../../environments/testExecutions/patternAnalysis';

interface TestLaneTabProps {
  pr: PullRequestDetails;
  lane: TestLaneDef;
  status: LaneStatus;
}

// Full run history for one PR/suite, newest first. The workflow context only keeps a recent global
// window; this per-PR/suite subscription backs the "previous runs" dropdown with the complete list.
function usePrSuiteExecutions(prNumber: number, suiteId: string) {
  const [executions, setExecutions] = useState<TestExecutionDocument[]>([]);
  useEffect(() => {
    let unsub: (() => void) | null = null;
    subscribeToTestExecutionsForPR(prNumber, all => {
      setExecutions(all.filter(e => e.testSuiteId === suiteId));
    }).then(u => { if (u) unsub = u; }).catch(err => console.error('Failed to load lane executions', err));
    return () => { if (unsub) unsub(); };
  }, [prNumber, suiteId]);
  return executions;
}

function statusChipColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'in_progress' || status === 'pending') return 'warning';
  return 'default';
}

const TestLaneTab: React.FC<TestLaneTabProps> = ({ pr, lane, status }) => {
  const prNumber = pr.pr.number;
  const executions = usePrSuiteExecutions(prNumber, lane.suiteId);
  const { liveDeploymentsByEnvironment } = useDeploymentStatus();
  const { config } = useConfig();
  const goldenBaselineId = config?.goldenBaselines?.[lane.suiteId]?.executionId;

  const required = laneRequired(lane, pr.changedFiles ?? []);

  // The latest run is what the tab summarizes; the dropdown selects any run to inspect.
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const latest = executions[0];
  const shown = executions.find(e => e.executionId === selectedExecutionId) ?? latest;

  // Environments where this PR is currently live — the only places a rerun can target.
  const liveEnvironments = useMemo(() => {
    const out: Array<{ environmentId: string; rolloutId: string }> = [];
    liveDeploymentsByEnvironment.forEach((live, environmentId) => {
      if (live.prNumber === prNumber) out.push({ environmentId, rolloutId: live.rolloutId });
    });
    return out;
  }, [liveDeploymentsByEnvironment, prNumber]);

  const [runEnv, setRunEnv] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<string | null>(null);
  const [waiving, setWaiving] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyNote, setVerifyNote] = useState('');
  const [verifying, setVerifying] = useState(false);

  const selectedRollout = liveEnvironments.find(e => e.environmentId === runEnv)?.rolloutId;
  const { testSuites } = useTestSuites(selectedRollout);
  const laneSuite = testSuites.find(s => s.id === lane.suiteId);

  const handleWaive = async () => {
    setWaiving(true);
    setActionError(null);
    try {
      await waiveTestRerun(prNumber, lane.suiteId);
      setActionInfo('Accepted the previous result for the current commit.');
    } catch (err: any) {
      setActionError(err.message || 'Failed to record the waiver');
    } finally {
      setWaiving(false);
    }
  };

  // The run whose results a verification would accept: the latest one, which is what drives the
  // lane's 'verify' status. Verification is recorded on this exact execution for audit.
  const runNeedingVerification = latest;
  const verifyWarnsOfErrors =
    !!runNeedingVerification && (runNeedingVerification.status === 'failed' || runNeedingVerification.failedTests > 0);

  // Pattern-verification gate: verifyTestExecution refuses to certify until every review item has an
  // accepted verdict, so mirror that here — show progress and only enable the button when complete.
  // The callable is the source of truth; this is just to stop the reviewer from a doomed click.
  const analysis = runNeedingVerification?.analysis;
  const analysisReady = analysis?.status === 'ready';
  const { acks: verifyAcks, error: verifyAcksError } = usePatternAcks(runNeedingVerification?.executionId, status === 'verify' && analysisReady);
  const itemTotal = analysis?.itemCount ?? 0;
  const acceptedCount = Object.values(verifyAcks).filter(a => a.verdict === 'accepted').length;
  const rejectedCount = Object.values(verifyAcks).filter(a => a.verdict === 'rejected').length;
  // A run with no analysis (e.g. web-e2e suites that don't recompute) has nothing to accept.
  const patternGateComplete = !analysis || (analysisReady && acceptedCount >= itemTotal && rejectedCount === 0);
  const patternsHref = runNeedingVerification
    ? `/environment/${runNeedingVerification.environment}/test-execution/${lane.suiteId}?executionId=${runNeedingVerification.executionId}#patterns`
    : '#';

  const handleVerifyConfirm = async () => {
    if (!runNeedingVerification) return;
    setVerifying(true);
    setActionError(null);
    try {
      await verifyTestExecution(runNeedingVerification.executionId, verifyNote.trim() || undefined);
      setActionInfo('Marked this run verified.');
      setVerifyOpen(false);
      setVerifyNote('');
    } catch (err: any) {
      setActionError(err.message || 'Failed to record the verification');
    } finally {
      setVerifying(false);
    }
  };

  const handleRunConfirm = async (subjects: string[], baselineExecutionId?: string, comments?: string, acknowledgedDataMismatch?: boolean) => {
    if (!selectedRollout) return;
    setDialogOpen(false);
    setActionError(null);
    try {
      await startTestExecution(lane.suiteId, selectedRollout, subjects, undefined, baselineExecutionId, comments, acknowledgedDataMismatch);
      setActionInfo(`Started ${lane.label} on ${runEnv}.`);
    } catch (err: any) {
      setActionError(err.message || `Failed to start ${lane.label}`);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header row: overall lane status + required badge + previous-runs picker */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {laneStatusGlyph(status, 18)}
        <Typography variant="body2" fontWeight={600}>{laneStatusLabel(status)}</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={required ? 'info' : 'default'}
          label={required ? 'Required for this PR' : 'Not required'}
        />
        <Box sx={{ flexGrow: 1 }} />
        {executions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id={`runs-${lane.id}`}>Run</InputLabel>
            <Select
              labelId={`runs-${lane.id}`}
              label="Run"
              value={shown?.executionId ?? ''}
              onChange={e => setSelectedExecutionId(e.target.value || null)}
            >
              {executions.map(e => (
                <MenuItem key={e.executionId} value={e.executionId}>
                  {formatDateTime(e.createdAt)} · {e.environment} · {e.status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
      {actionInfo && <Alert severity="success" onClose={() => setActionInfo(null)}>{actionInfo}</Alert>}

      {/* Verify decision: a finished run waits for a human to inspect its results before it counts. */}
      {status === 'verify' && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setVerifyOpen(true)}
              disabled={!patternGateComplete}
            >
              Mark verified…
            </Button>
          }
        >
          This {lane.label} has finished. Review every change pattern
          {analysis && (
            <>
              {' '}(
              <Link component={RouterLink} to={patternsHref} color="inherit">
                {analysisReady
                  ? `${acceptedCount} of ${itemTotal} accepted`
                  : 'analysis pending…'}
              </Link>
              )
            </>
          )}
          , then mark it verified — or rerun it.
          {analysis && rejectedCount > 0 && (
            <> {rejectedCount} item{rejectedCount === 1 ? ' is' : 's are'} rejected; resolve or rerun before verifying.</>
          )}
        </Alert>
      )}

      {/* Retest decision */}
      {status === 'retest-decision' && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleWaive} disabled={waiving}>
              {waiving ? <CircularProgress size={16} /> : 'Accept without rerun'}
            </Button>
          }
        >
          New commits landed after the last {lane.verify ? 'verified' : 'passing'} {lane.label}. Accept the prior result, or rerun below.
        </Alert>
      )}

      {/* Latest / selected run card */}
      {shown ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip size="small" color={statusChipColor(shown.status)} label={shown.status} />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>{shown.environment}</Typography>
            <Link
              component={RouterLink}
              to={`/environment/${shown.environment}/test-execution/${lane.suiteId}?executionId=${shown.executionId}`}
            >
              Open run
            </Link>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Started {formatDateTime(shown.startedAt || shown.createdAt)} · {shown.passedTests}/{shown.totalTests} passed
            {shown.failedTests > 0 ? ` · ${shown.failedTests} failed` : ''}
          </Typography>
          {shown.artifacts && shown.artifacts.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {shown.artifacts.map((a, i) => (
                <Link key={i} href={a.url} target="_blank" rel="noopener noreferrer" variant="body2">
                  {a.description || a.type}
                </Link>
              ))}
            </Box>
          )}
          {shown.verification && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
              {laneStatusGlyph('verify', 16)}
              <Typography variant="caption" color="text.secondary">
                Verified by{' '}
                <UserProfileDisplay uid={shown.verification.verifiedBy} variant="caption" color="text.secondary" />
                {' '}on {formatDateTime(shown.verification.verifiedAt)}
                {shown.verification.note ? ' — ' + shown.verification.note : ''}
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary">No {lane.label} has run for this PR yet.</Typography>
      )}

      <Divider />

      {/* Run on an environment */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Run {lane.label}</Typography>
        {liveEnvironments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            This PR isn&apos;t deployed anywhere. Deploy it from the Environments tab first, then run the test here.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id={`runenv-${lane.id}`}>Environment</InputLabel>
              <Select
                labelId={`runenv-${lane.id}`}
                label="Environment"
                value={runEnv}
                onChange={e => setRunEnv(e.target.value)}
              >
                {liveEnvironments.map(e => (
                  <MenuItem key={e.environmentId} value={e.environmentId}>{e.environmentId}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              disabled={!runEnv || !laneSuite}
              onClick={() => setDialogOpen(true)}
            >
              Run…
            </Button>
            {runEnv && !laneSuite && (
              <Typography variant="caption" color="text.secondary">
                Loading suite for {runEnv}…
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {laneSuite && (
        <TestSubjectSelectionDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          testSuite={laneSuite}
          environmentId={runEnv}
          onConfirm={handleRunConfirm}
          goldenBaselineId={goldenBaselineId}
        />
      )}

      <Dialog open={verifyOpen} onClose={() => setVerifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify {lane.label}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirm you have inspected this run's results (diffs against the baseline and Error Logs)
            and accept them. This is recorded against you for audit.
          </DialogContentText>
          {verifyWarnsOfErrors && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This run reported errors or failed steps. Verifying accepts those results anyway — only
              do so if you have confirmed they are expected.
            </Alert>
          )}
          {verifyAcksError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Couldn't load pattern acknowledgements ({verifyAcksError}). Verification stays blocked
              until they load — try reopening this panel.
            </Alert>
          )}
          {!patternGateComplete && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {analysisReady
                ? `Not every change pattern has been accepted (${acceptedCount} of ${itemTotal}${rejectedCount > 0 ? `, ${rejectedCount} rejected` : ''}). Verification will be rejected until all are accepted.`
                : 'Pattern analysis is still running. Verification is unavailable until it completes.'}
            </Alert>
          )}
          <TextField
            label="Note (optional)"
            value={verifyNote}
            onChange={e => setVerifyNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            placeholder="e.g. diffs are known compute-timestamp masks"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyOpen(false)} disabled={verifying}>Cancel</Button>
          <Button variant="contained" onClick={handleVerifyConfirm} disabled={verifying || !patternGateComplete}>
            {verifying ? <CircularProgress size={16} /> : 'Mark verified'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestLaneTab;
